import { Box, Button, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
import { F0TagRaw } from '@factorialco/f0-react'
import { RichTextDisplay } from '@factorialco/f0-react/dist/experimental'
import { EyeVisible } from '@factorialco/f0-react/icons/app'
import { AtsFeedbackDetailFragment } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import { AttachedAtsFile } from 'modules/ats/components/AttachedAtsFile'
import { FeedbackAuthor } from 'modules/ats/components/Feedbacks/FeedbackAuthor'
import { useOnFeedbackDelete } from 'modules/ats/lib/utils/feedbacks'

interface Props {
  feedback: AtsFeedbackDetailFragment
  onEdit: () => void
  restrictedCommentsAvailable?: boolean
}

export const FeedbackDisplay = ({ feedback, onEdit, restrictedCommentsAvailable }: Props) => {
  const { isDeleting, onFeedbackDelete } = useOnFeedbackDelete(feedback)
  const [showRestrictedVisibilityTooltip, toggleRestrictedVisibilityTooltip] = useToggle(false)
  const [deleteModalOpen, toggleDeleteModalOpen] = useToggle(false)
  const { access, atsFiles, description, visibility } = feedback
  const {
    access: { id: accessId },
  } = useCurrentSession()
  const isFeedbacksAuthor = access?.id === accessId

  const hasRestrictedVisibility = visibility === 'restricted'

  return (
    <Box width="full" gap="s24">
      <Box gap="s16">
        <Box flexDirection="row" alignItems="center" justifyContent="spaceBetween">
          <FeedbackAuthor feedback={feedback} />
          <Box flexDirection="row" alignItems="center" gap="s8">
            {hasRestrictedVisibility && restrictedCommentsAvailable && (
              <Tooltip.Alt
                open={showRestrictedVisibilityTooltip}
                toggle={toggleRestrictedVisibilityTooltip}
                label={i18n.t('ats.feedbacks.restricted_visibility')}
                attachment="bottom center"
              >
                <F0TagRaw icon={EyeVisible} />
              </Tooltip.Alt>
            )}

            {isFeedbacksAuthor && (
              <PopoverMenu type="list">
                <PopoverMenu.Trigger>
                  {/* TODO: Add aria label */}
                  <Button.Decorative type="secondary" iconName="ellipsis" />
                </PopoverMenu.Trigger>
                <PopoverMenu.Content side="bottom" align="center" disablePortal>
                  <PopoverMenu.TouchableOption title={i18n.t('common.edit')} onClick={onEdit} />
                  <PopoverMenu.TouchableOption
                    danger
                    title={i18n.t('common.destroy')}
                    onClick={toggleDeleteModalOpen(true)}
                  />
                </PopoverMenu.Content>
              </PopoverMenu>
            )}
          </Box>
        </Box>

        {description && <RichTextDisplay content={description} />}
      </Box>

      {atsFiles.length > 0 && (
        <Box flexDirection="row" gap="s16" flexWrap="wrap">
          {atsFiles.map((atsFile) => (
            <AttachedAtsFile
              key={atsFile.id}
              filename={atsFile.file.filename}
              url={atsFile.file.url}
              fileId={atsFile.id}
            />
          ))}
        </Box>
      )}

      {deleteModalOpen && (
        <Modal
          illustration="network-group"
          title={i18n.t('ats.feedbacks.delete_feedback')}
          description={i18n.t('ats.feedbacks.confirm_delete')}
          onClose={toggleDeleteModalOpen(false)}
          onSubmit={onFeedbackDelete}
          footer={
            <Button type="negative" disabled={isDeleting} submit>
              {i18n.t('ats.feedbacks.delete_feedback')}
            </Button>
          }
        />
      )}
    </Box>
  )
}
